import { useEffect, useId, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { UserSearchItem } from "src/components";
import ResultCountAnnouncer from "src/components/ResultCountAnnouncer/ResultCountAnnouncer";
import { useStoreUser } from "src/hooks";
import { useDebounce } from "src/libs/hooks";
import { AxiosError } from "axios";

interface UserSearchProps {
	value?: string;
	onChange: (user: Partial<StoreUser>) => void;
}

/**
 * Type-ahead picker for retail-store users. See the matching note on
 * `containers/StoreSearch.tsx` — the four pickers (this, StoreSearch,
 * reporting EmployeeSearch, reporting StoreSearch) deliberately
 * share the same combobox shape, focus-management rules, and
 * keyboard interaction model.
 */
const UserSearch = ({ value, onChange }: UserSearchProps) => {
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<
		string | undefined
	>();
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);

	const listInnerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const lastSelectedNameRef = useRef<string | null>(null);
	// The clear button refocuses the input, which fires onFocus while the
	// debounced query is still the stale pre-clear value. Without this flag
	// onFocus would re-open the listbox we just closed.
	const skipNextFocusOpenRef = useRef(false);
	const listboxId = useId();

	const {
		data,
		isError,
		error,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage
	} = useStoreUser(debouncedSearchValue, {
		enabled: Boolean(debouncedSearchValue)
	});

	useDebounce(() => setDebouncedSearchValue(value), 300, [value]);

	const users = data ? data.pages.flatMap(({ results }) => results) : [];
	const isLoading = isFetching && Boolean(debouncedSearchValue);
	const isPopupOpen = isOpen && Boolean(debouncedSearchValue);

	useEffect(() => {
		setActiveIndex(-1);
		if (debouncedSearchValue !== lastSelectedNameRef.current) {
			lastSelectedNameRef.current = null;
		}
		if (
			debouncedSearchValue &&
			debouncedSearchValue !== lastSelectedNameRef.current
		) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	}, [debouncedSearchValue]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
				setActiveIndex(-1);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const handleScroll = () => {
		if (listInnerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
			if (scrollTop + clientHeight >= scrollHeight - 1) {
				hasNextPage && fetchNextPage();
			}
		}
	};

	const handleSelect = (user: StoreUser) => {
		lastSelectedNameRef.current = user.name ?? null;
		onChange(user);
		setIsOpen(false);
		setActiveIndex(-1);
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && isPopupOpen) {
			e.preventDefault();
			setIsOpen(false);
			setActiveIndex(-1);
			return;
		}

		if (
			e.key === "ArrowDown" &&
			!isPopupOpen &&
			debouncedSearchValue &&
			users.length > 0
		) {
			e.preventDefault();
			setIsOpen(true);
			setActiveIndex(0);
			return;
		}

		if (!isPopupOpen || users.length === 0) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((prev) => (prev < users.length - 1 ? prev + 1 : 0));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((prev) => (prev > 0 ? prev - 1 : users.length - 1));
				break;
			case "Home":
				e.preventDefault();
				setActiveIndex(0);
				break;
			case "End":
				e.preventDefault();
				setActiveIndex(users.length - 1);
				break;
			case "Enter":
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < users.length) {
					handleSelect(users[activeIndex]);
				}
				break;
		}
	};

	const activeOptionId =
		activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

	if (isError && error)
		return <p className="text-red-600">{(error as AxiosError).message}</p>;

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<Search
					aria-hidden="true"
					className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
				/>
				<input
					ref={inputRef}
					type="text"
					placeholder="Search by employee name..."
					value={value}
					onChange={({ target }: React.ChangeEvent<HTMLInputElement>) =>
						onChange({ name: target.value })
					}
					onFocus={() => {
						if (skipNextFocusOpenRef.current) {
							skipNextFocusOpenRef.current = false;
							return;
						}
						if (debouncedSearchValue) setIsOpen(true);
					}}
					onKeyDown={handleKeyDown}
					role="combobox"
					aria-expanded={isPopupOpen}
					aria-haspopup="listbox"
					aria-autocomplete="list"
					aria-controls={listboxId}
					aria-activedescendant={activeOptionId}
					aria-label="Search by employee name"
					className="w-full bg-white border border-border rounded-lg pl-9 pr-10 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
				/>

				{isFetchingNextPage && (
					<span className="absolute right-9 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin block" />
				)}

				{value !== undefined && value !== "" && (
					<button
						type="button"
						onClick={() => {
							lastSelectedNameRef.current = null;
							// Close the popup and clear activedescendant synchronously —
							// otherwise the debounced state lags ~300 ms and the listbox
							// keeps showing stale results with a stale aria-activedescendant.
							setIsOpen(false);
							setActiveIndex(-1);
							onChange({ name: "" });
							skipNextFocusOpenRef.current = true;
							inputRef.current?.focus();
						}}
						className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
						aria-label="Clear search"
					>
						<X className="h-4 w-4" aria-hidden="true" />
					</button>
				)}
			</div>

			<ResultCountAnnouncer
				isSearching={isLoading}
				count={users.length}
				isOpen={isPopupOpen}
				subjectSingular="employee"
				subjectPlural="employees"
				query={debouncedSearchValue ?? ""}
			/>

			{isPopupOpen && (
				<div
					ref={listInnerRef}
					onScroll={handleScroll}
					id={listboxId}
					role="listbox"
					aria-label="Retail store users"
					className="absolute z-10 mt-1 bg-white border border-border rounded-xl shadow-md max-h-60 overflow-y-auto w-full"
				>
					{users.length === 0 && !isLoading ? (
						<div className="px-3 py-3 text-sm text-muted-foreground">
							No employees found
						</div>
					) : (
						users.map((user, index) => (
							<div
								key={`option-${user.uuid}`}
								id={`${listboxId}-option-${index}`}
								role="option"
								aria-selected={index === activeIndex}
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => handleSelect(user)}
								className={[
									"w-full text-left px-3 py-2.5 text-sm text-foreground transition-colors first:rounded-t-xl last:rounded-b-xl cursor-pointer",
									index === activeIndex
										? "bg-primary-lighter"
										: "hover:bg-primary-lighter"
								].join(" ")}
							>
								<UserSearchItem user={user} icon={null} />
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};

export default UserSearch;
